import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { FUNCTION_RESULT_STATUS } from '@nova/common-interfaces';
import { ISubscription } from '@rfm/shared-interfaces';
import { BlockUIModule } from 'ng-block-ui';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { SelectButtonModule } from 'primeng/selectbutton';
import { Table, TableModule } from 'primeng/table';
import { TabMenuModule } from 'primeng/tabmenu';
import { ToastModule } from 'primeng/toast';
import { CommonComponent } from '../../../../../lib/components/index.mjs';
import { BoolToTextPipe } from '../../../../../lib/pipes/bool-to-text.pipe.mjs';
import { SubscriptionService } from '../../../../shared/services/products/subscription.service.mjs';
import { SubscriptionChangePlanModal } from '../subscription-change-plan/subscription-change-plan.modal';
import { SubscriptionChangePriceModal } from '../subscription-change-price/subscription-change-price.modal';

@Component({
  selector: 'app-subscription-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SelectButtonModule,
    TableModule,
    BlockUIModule,
    ToastModule,
    BoolToTextPipe,
    IconFieldModule,
    InputTextModule,
    InputIconModule,
    ConfirmDialogModule,
    TabMenuModule,
    MenuModule,
    ButtonModule,
    SubscriptionChangePriceModal,
    SubscriptionChangePlanModal,
  ],
  providers: [
    ConfirmationService,
    MessageService
  ],
  templateUrl: './subscription-list.component.html',
  styleUrls: ['./subscription-list.component.scss'],
})
export class SubscriptionListComponent
  extends CommonComponent
  implements OnInit
{
  objectType = signal('subscription');
  objectId = signal('');
  subscriptions: ISubscription[] = [];
  filtered_subscriptions: ISubscription[] = [];
  activeItem: ISubscription | undefined;
  changePriceVisible = signal(false);
  changePlanVisible = signal(false);
  activeTab: any;
  activeTabLabel: string = "active";
  loading=true;


  row_items: MenuItem[] = [];
    items: MenuItem[] = [
    {
      label: 'View',
      icon: 'pi pi-file',
      command: () => this.view(this.activeItem!.id),
    },
    {
      label: 'View Customer',
      icon: 'pi pi-file-plus',
      command: () => this.showAll(this.activeItem!.customer_id.id),
    },
    {
      label: 'Edit',
      icon: 'pi pi-file-edit',
      command: () => this.edit(this.activeItem!.id),
    },
    {
      label: 'Suspend',
      icon: 'pi pi-pause-circle',
      command: () => this.suspend(event, this.activeItem!.id),
    },
    {
      label: 'Activate',
      icon: 'pi pi-play-circle',
      command: () => this.activate(this.activeItem!.id),
    },
    {
      label: 'Cancel',
      icon: 'pi pi-times-circle',
      command: () => this.cancel(this.activeItem!.id),
    },
    {
      label: 'Change Plan',
      icon: 'pi pi-cart-plus',
      command: () => this.changePlanVisible.set(true),
    },
    {
      label: 'Change Price',
      icon: 'pi pi-dollar',
      command: () => this.changePriceVisible.set(true),
    },
  ];

  tabs: MenuItem[] = [
    { label: "Active", command: () => this.setActiveTab("active") },
    { label: "Due", command: () => this.setActiveTab("due") },
    { label: "Inactive", command: () => this.setActiveTab("inactive") },
    { label: "Suspended", command: () => this.setActiveTab("suspended") },
    { label: "Cancelled", command: () => this.setActiveTab("cancelled") }
  ];

  addSubscription() {
    this.router.navigate(['/admin/subscriptions/new']);
  }

  showAll(customer_id: string) {
    this.router.navigateByUrl('/admin/customers/view/' + customer_id);
  }
  
  setActiveTab(label: string) {
    this.activeTabLabel = label;
    this.applyFilter();
  }

  filter(event:any, table: Table)
  {
    table.filterGlobal(event.target.value, 'contains');
  }

  searchValue: string | undefined;

  clear(table: Table) {
    table.clear();
    this.searchValue = ''
  }
  
  view(subscription_id: string) {
    this.router.navigateByUrl('/admin/subscriptions/view/' + subscription_id);
  }

  edit(subscription_id: string) {
    this.router.navigateByUrl('/admin/subscriptions/edit/' + subscription_id);
  }

  async activate(subscription_id: string) {
    this.confirmationService.confirm({
      target: event!.target as EventTarget,
      message: 'Are you sure that you want to activate this subscription?',
      header: 'Activate Subscription',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Activate Subscription',
      },
      accept: async () => {
        //*************************************
        //TODO: add workflow to credit and debit customer account for plan change
        // */
        this.isLoading(true);

        await this.subscriptionService.activate(subscription_id).then(result => {
          if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS)
          {
            this.messageService.add({
              severity: 'error',
              summary: 'Subscription was not activated',
              detail: 'Subscription has not been changed',
              life: 3000,
            });
            this.isLoading(false);
            return Promise.reject(result);
          }

          this.messageService.add({
            severity: 'success',
            summary: 'Subscription Activated',
            detail: 'Subscription has been activated successfully!',
            life: 3000,
          });
          this.isLoading(false);
          this.handleData(true);
          return Promise.resolve();
        })
        .catch(err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Subscription Not Activated',
            detail: 'Subscription has not been activated',
            life: 3000,
          });
          this.isLoading(false);
          return Promise.reject(err);
        })
        .finally(() => {
          this.isLoading(false);
        });
      },
      reject: () => {
        this.isLoading(false);
      },
    });
  }

  async suspend(event: any, subscription_id: string) {
    console.log("Suspend");
    this.confirmationService.confirm({
      target: event!.target as EventTarget,
      message: 'Are you sure that you want to suspend this subscription?',
      header: 'Suspend Subscription',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Suspend Subscription',
      },
      accept: async () => {
        //*************************************
        //TODO: add workflow to credit and debit customer account for plan change
        // */
        this.isLoading(true);

        await this.subscriptionService.suspend(subscription_id).then(result => {
          if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS)
          {
            this.messageService.add({
              severity: 'error',
              summary: 'Subscription was not suspended',
              detail: 'Subscription has not been changed',
              life: 3000,
            });
            this.isLoading(false);
            return Promise.reject(result);
          }

          this.messageService.add({
            severity: 'success',
            summary: 'Subscription Suspended',
            detail: 'Subscription has been suspended successfully!',
            life: 3000,
          });
          this.isLoading(false);
          this.handleData(true);
          return Promise.resolve();
        })
        .catch(err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Subscription Not Suspended',
            detail: 'Subscription has not been suspended',
            life: 3000,
          });
          this.isLoading(false);
          return Promise.reject(err);
        })
        .finally(() => {
          this.isLoading(false);
        });
      },
      reject: () => {
        this.isLoading(false);
      },
    });
  }

  async cancel(subscription_id: string) {
    this.confirmationService.confirm({
      target: event!.target as EventTarget,
      message: 'Are you sure that you want to cancel this subscription?',
      header: 'Cancel Subscription',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Cancel Subscription',
      },
      accept: async () => {
        //*************************************
        //TODO: add workflow to credit and debit customer account for plan change
        // */
        this.isLoading(true);

        await this.subscriptionService.cancel(subscription_id).then(result => {
          if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS)
          {
            this.messageService.add({
              severity: 'error',
              summary: 'Subscription was not cancelled',
              detail: 'Subscription has not been changed',
              life: 3000,
            });
            this.isLoading(false);
            return Promise.reject(result);
          }

          this.messageService.add({
            severity: 'success',
            summary: 'Subscription Cancelled',
            detail: 'Subscription has been cancelled successfully!',
            life: 3000,
          });
          this.isLoading(false);
          this.handleData(true);
          return Promise.resolve();
        })
        .catch(err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Subscription Not Cancelled',
            detail: 'Subscription has not been cancelled',
            life: 3000,
          });
          this.isLoading(false);
          return Promise.reject(err);
        })
        .finally(() => {
          this.isLoading(false);
        });
      },
      reject: () => {
        this.isLoading(false);
      },
    });
  }

  constructor(
    protected router: Router,
    protected subscriptionService: SubscriptionService,
    protected confirmationService: ConfirmationService,
    protected messageService: MessageService
  ) {
    super();

    this.isLoading$.subscribe(state => {
      this.loading = state;
    })

    this.isLoading(true);
  }

  menuClicked(rowData: any) {
    this.activeItem = rowData;
    this.row_items = [];

    this.items.forEach((item) => {
      switch (item.id) {
        case 'suspend':
          if (this.activeItem?.is_active) {
            this.row_items.push(item);
          }
          return item;
          case 'activate':
            if (!this.activeItem?.is_active) {
              this.row_items.push(item);
            }
            return item;
        case 'change_price':
          if (this.activeItem?.is_active) {
            this.row_items.push(item);
          }
          return item;
        case 'change_plan':
          if (this.activeItem?.is_active) {
            this.row_items.push(item);
          }
          return item;
        default:
          this.row_items.push(item);
          return item;
      }
    });
    
    this.objectId.set(rowData.id);
  }
 
  applyFilter() {
    if (this.activeTabLabel === 'active') {
      this.filtered_subscriptions = this.subscriptions.filter(
        (x) => x.is_active && x.status !== "cancelled"
      );
    } else if (this.activeTabLabel === 'due') {
      this.filtered_subscriptions = this.subscriptions.filter(
        (x) => x.is_active && x.status === "active" && x.next_due <= Date.now()
      );
    } else if (this.activeTabLabel === 'inactive') {
      this.filtered_subscriptions = this.subscriptions.filter(
        (x) => x.is_active !== true && x.status !== "cancelled"
      );
    } else if (this.activeTabLabel === 'cancelled') {
      this.filtered_subscriptions = this.subscriptions.filter(
        (x) => x.status === "cancelled"
      );
    } else if (this.activeTabLabel === 'suspended') {
      this.filtered_subscriptions = this.subscriptions.filter(
        (x) => x.status === "suspended"
      );
    } else {
      this.filtered_subscriptions = this.subscriptions;
    }
  }
  
  selectedSubscription: ISubscription | undefined;

  rowSelected() {
    console.log(this.selectedSubscription?.id!);
    this.objectId.set(this.selectedSubscription?.id!);
    this.router.navigate([
      '/admin/subscriptions/view/',
      this.selectedSubscription?.id,
    ]);
  }

  ngOnInit() {
    this.handleData(true);
  }

  handleData(data: any) {
    this.subscriptionService
      .getAll()
      .then((result) => {
        if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
          this.subscriptions = result.data!;
          if (result.options.pageToken)
          {
            console.log("Page Token: " + result.options.pageToken);
          }
          this.applyFilter();
        }
      })
      .finally(() => {
        this.isLoading(false);
      });
  }
}
